// client/src/pages/Knowledge.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { KNOWLEDGE_CATEGORIES, ARTICLES, GLOSSARY, TOOLS_DATA } from '../data/knowledgeData';

const LEVELS = { 'Beginner': '#16a34a', 'Intermediate': '#d97706', 'Advanced': '#dc2626' };

export default function Knowledge() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.subscription?.status === 'active';
  const [activeTab, setActiveTab] = useState('encyclopedia');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [glossarySearch, setGlossarySearch] = useState('');
  const [activeTool, setActiveTool] = useState(null);

  const filteredArticles = ARTICLES.filter(a => {
    const matchCat = selectedCategory === 'all' || a.category === selectedCategory;
    const matchSearch = !searchQuery || a.title.toLowerCase().includes(searchQuery.toLowerCase()) || a.tags.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchCat && matchSearch;
  });

  const filteredGlossary = GLOSSARY.filter(g =>
    !glossarySearch || g.term.toLowerCase().includes(glossarySearch.toLowerCase()) || g.definition.toLowerCase().includes(glossarySearch.toLowerCase())
  );

  const tabs = [
    { id: 'encyclopedia', label: '📚 Encyclopedia', count: ARTICLES.length },
    { id: 'tools', label: '🛠️ Tools', count: TOOLS_DATA.length },
    { id: 'glossary', label: '📖 Glossary', count: GLOSSARY.length },
  ];

  return (
    <div style={{ minHeight: 'calc(100vh - 56px)', background: 'var(--paper)' }}>
      {/* Hero */}
      <div style={{ background: 'var(--ink)', color: 'var(--paper)', padding: '52px 24px 44px', borderBottom: '2px solid var(--ink)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '3px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 12 }}>Clinical Trial Knowledge Center</div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(28px, 5vw, 52px)', lineHeight: 1.05, marginBottom: 14 }}>
            The Clinical Trials <em>Encyclopedia</em>
          </h1>
          <p style={{ color: 'var(--muted-2)', fontSize: 15, maxWidth: 580, lineHeight: 1.65, marginBottom: 28 }}>
            Advanced-level knowledge for clinical researchers, trial managers, regulatory professionals, and anyone building expertise in clinical trials. From GCP fundamentals to adaptive statistics.
          </p>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {[['📚', `${ARTICLES.length} Articles`], ['🛠️', `${TOOLS_DATA.length} Tools`], ['📖', `${GLOSSARY.length} Terms`], ['🔓', 'Free Access']].map(([icon, label]) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--muted-2)' }}>
                <span>{icon}</span>{label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pro Banner */}
      {!isPro && (
        <div style={{ background: 'var(--ink-2)', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--paper)', fontSize: 13, fontFamily: 'var(--font-mono)' }}>⭐ Pro members unlock advanced articles, AI tools, statistical calculators, and protocol builders</span>
          <button onClick={() => navigate('/profile')} style={{ background: 'var(--paper)', color: 'var(--ink)', border: 'none', padding: '7px 18px', borderRadius: 0, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
            Unlock Pro →
          </button>
        </div>
      )}

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>
        {/* Tabs */}
        <div className="knowledge-tabs" style={{ display: 'flex', gap: 0, marginBottom: 32, borderBottom: '2px solid var(--line)' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ padding: '12px 24px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: activeTab === tab.id ? 'var(--ink)' : 'var(--muted)', borderBottom: `2px solid ${activeTab === tab.id ? 'var(--ink)' : 'transparent'}`, marginBottom: -2, transition: 'all 0.15s', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap' }}>
              {tab.label} <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted-2)', marginLeft: 4 }}>({tab.count})</span>
            </button>
          ))}
        </div>

        {/* ── ENCYCLOPEDIA TAB ── */}
        {activeTab === 'encyclopedia' && (
          <div>
            {/* Category Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14, marginBottom: 36 }}>
              {KNOWLEDGE_CATEGORIES.map(cat => (
                <div key={cat.id}
                  onClick={() => setSelectedCategory(selectedCategory === cat.id ? 'all' : cat.id)}
                  style={{ background: selectedCategory === cat.id ? 'var(--ink)' : 'var(--card)', border: `1px solid ${selectedCategory === cat.id ? 'var(--ink)' : 'var(--line-card)'}`, borderRadius: 0, padding: '16px 18px', cursor: 'pointer', transition: 'all 0.15s', position: 'relative' }}
                  onMouseEnter={e => { if (selectedCategory !== cat.id) { e.currentTarget.style.borderColor = 'var(--ink)'; e.currentTarget.style.boxShadow = '6px 6px 0 rgba(10,10,10,0.06)'; } }}
                  onMouseLeave={e => { if (selectedCategory !== cat.id) { e.currentTarget.style.borderColor = 'var(--line-card)'; e.currentTarget.style.boxShadow = 'none'; } }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ fontSize: 22 }}>{cat.icon}</div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {cat.pro && <span style={{ background: selectedCategory === cat.id ? '#ffffff22' : 'var(--paper-2)', color: selectedCategory === cat.id ? 'var(--paper)' : 'var(--ink)', fontSize: 9, padding: '1px 6px', borderRadius: 0, fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>PRO</span>}
                      <span style={{ background: selectedCategory === cat.id ? '#ffffff22' : 'var(--paper-2)', color: selectedCategory === cat.id ? '#ddd' : 'var(--muted-2)', fontSize: 9, padding: '1px 6px', borderRadius: 0, fontFamily: 'var(--font-mono)' }}>{cat.articleCount} articles</span>
                    </div>
                  </div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 700, color: selectedCategory === cat.id ? 'var(--paper)' : 'var(--ink)', marginBottom: 5, lineHeight: 1.3 }}>{cat.title}</div>
                  <div style={{ fontSize: 11, color: selectedCategory === cat.id ? '#999' : 'var(--muted)', lineHeight: 1.5 }}>{cat.description}</div>
                </div>
              ))}
            </div>

            {/* Search + Filter Bar */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search articles, topics, keywords..."
                onFocus={e => { e.currentTarget.style.borderColor = 'var(--ink)'; }}
                onBlur={e => { e.currentTarget.style.borderColor = 'var(--line)'; }}
                style={{ flex: 1, minWidth: 240, padding: '9px 14px', border: '1px solid var(--line)', borderRadius: 0, fontSize: 14, outline: 'none', fontFamily: 'var(--font-body)' }}
              />
              {selectedCategory !== 'all' && (
                <button onClick={() => setSelectedCategory('all')} style={{ fontSize: 12, padding: '8px 14px', border: '1px solid var(--line)', background: 'var(--card)', borderRadius: 0, cursor: 'pointer', fontFamily: 'var(--font-mono)' }}>
                  Clear filter ×
                </button>
              )}
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--muted-2)' }}>{filteredArticles.length} articles</span>
            </div>

            {/* Article Grid */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filteredArticles.map(article => {
                const locked = article.pro && !isPro;
                const cat = KNOWLEDGE_CATEGORIES.find(c => c.id === article.category);
                return (
                  <div key={article.slug}
                    onClick={() => locked ? navigate('/profile') : navigate(`/knowledge/${article.slug}`)}
                    style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 16, transition: 'all 0.12s', position: 'relative' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--ink)'; e.currentTarget.style.background = locked ? 'var(--paper)' : 'var(--card)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--line-card)'; e.currentTarget.style.background = 'var(--card)'; }}>
                    <div style={{ fontSize: 22, flexShrink: 0, marginTop: 2 }}>{cat?.icon || '📄'}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ background: 'var(--paper-2)', color: 'var(--ink)', fontSize: 10, padding: '2px 8px', borderRadius: 0, fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>{cat?.title}</span>
                        <span style={{ background: LEVELS[article.level] + '22', color: LEVELS[article.level], fontSize: 10, padding: '2px 8px', borderRadius: 0, fontFamily: 'var(--font-mono)' }}>{article.level}</span>
                        <span style={{ fontSize: 11, color: 'var(--muted-2)', fontFamily: 'var(--font-mono)' }}>{article.readTime} min read</span>
                        {locked && <span style={{ background: 'var(--ink)', color: 'var(--paper)', fontSize: 10, padding: '2px 8px', borderRadius: 0, fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>🔒 PRO</span>}
                      </div>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: locked ? 'var(--muted)' : 'var(--ink)', marginBottom: 4, lineHeight: 1.35 }}>{article.title}</div>
                      <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.55 }}>{article.summary}</div>
                    </div>
                    <div style={{ fontSize: 12, color: locked ? 'var(--muted-2)' : 'var(--ink)', fontFamily: 'var(--font-mono)', flexShrink: 0, fontWeight: 600 }}>
                      {locked ? '🔒 Pro' : 'Read →'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── TOOLS TAB ── */}
        {activeTab === 'tools' && (
          <div>
            {activeTool ? (
              <ToolRenderer tool={activeTool} onBack={() => setActiveTool(null)} isPro={isPro} navigate={navigate} />
            ) : (
              <>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
                    {TOOLS_DATA.filter(t => !t.pro).length} free tools · {TOOLS_DATA.filter(t => t.pro).length} pro tools
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
                  {TOOLS_DATA.map(tool => {
                    const locked = tool.pro && !isPro;
                    return (
                      <div key={tool.id}
                        onClick={() => locked ? navigate('/profile') : setActiveTool(tool)}
                        style={{ background: 'var(--card)', border: `1px solid var(--line-card)`, borderRadius: 0, padding: '20px 22px', cursor: 'pointer', transition: 'all 0.15s', opacity: locked ? 0.85 : 1 }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--ink)'; e.currentTarget.style.boxShadow = '6px 6px 0 rgba(10,10,10,0.06)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--line-card)'; e.currentTarget.style.boxShadow = 'none'; }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                          <span style={{ fontSize: 28 }}>{tool.icon}</span>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                            <span style={{ background: 'var(--paper-2)', color: 'var(--muted)', fontSize: 10, padding: '2px 8px', borderRadius: 0, fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>{tool.category}</span>
                            {locked && <span style={{ background: 'var(--ink)', color: 'var(--paper)', fontSize: 10, padding: '2px 8px', borderRadius: 0, fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>🔒 PRO</span>}
                          </div>
                        </div>
                        <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 6, lineHeight: 1.3 }}>{tool.title}</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.55, marginBottom: 14 }}>{tool.description}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)', fontFamily: 'var(--font-mono)' }}>
                          {locked ? '🔒 Unlock with Pro →' : 'Open Tool →'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── GLOSSARY TAB ── */}
        {activeTab === 'glossary' && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <input
                value={glossarySearch}
                onChange={e => setGlossarySearch(e.target.value)}
                placeholder="Search terms (e.g. randomization, ITT, SUSAR)..."
                onFocus={e => { e.currentTarget.style.borderColor = 'var(--ink)'; }}
                onBlur={e => { e.currentTarget.style.borderColor = 'var(--line)'; }}
                style={{ width: '100%', maxWidth: 480, padding: '10px 14px', border: '1px solid var(--line)', borderRadius: 0, fontSize: 14, outline: 'none', fontFamily: 'var(--font-body)', boxSizing: 'border-box' }}
              />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--muted-2)', marginLeft: 12 }}>{filteredGlossary.length} terms</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(440px, 1fr))', gap: 2 }}>
              {filteredGlossary.map(item => (
                <div key={item.term} style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '14px 18px' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 5, fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>{item.term}</div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>{item.definition}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── TOOL RENDERER ──────────────────────────────────────────────────
function ToolRenderer({ tool, onBack, isPro, navigate }) {
  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--muted)', marginBottom: 20, padding: 0, letterSpacing: '1px' }}>
        ← BACK TO TOOLS
      </button>
      <div style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '32px', maxWidth: 800 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
          <span style={{ fontSize: 32 }}>{tool.icon}</span>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>{tool.title}</div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>{tool.description}</div>
          </div>
        </div>
        {tool.id === 'sample-size-basic' && <SampleSizeCalculator />}
        {tool.id === 'ae-grading' && <AEGradingTool />}
        {tool.id === 'phase-guide' && <PhaseGuide />}
        {tool.id === 'glossary' && <p style={{ color: 'var(--muted)' }}>See the Glossary tab for full glossary access.</p>}
        {tool.pro && !isPro && (
          <div style={{ textAlign: 'center', padding: '40px 24px', background: 'var(--paper-2)', borderRadius: 0 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, marginBottom: 8 }}>Pro Feature</div>
            <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 20 }}>Upgrade to Pro to access this tool and all advanced features.</p>
            <button onClick={() => navigate('/profile')} style={{ background: 'var(--ink)', color: 'var(--paper)', border: 'none', padding: '11px 28px', borderRadius: 0, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Upgrade to Pro →</button>
          </div>
        )}
        {tool.id === 'power-calculator' && isPro && <AdvancedPowerCalculator />}
        {tool.id === 'protocol-builder' && isPro && <AIProtocolBuilder />}
        {tool.id === 'irb-checklist' && isPro && <AIIRBChecklist />}
        {tool.id === 'consort-checklist' && isPro && <CONSORTChecklist />}
        {tool.id === 'randomization-calculator' && isPro && <RandomizationGenerator />}
        {tool.id === 'risk-matrix' && isPro && <RiskMatrix />}
      </div>
    </div>
  );
}


// ── MARKDOWN RENDERER ────────────────────────────────────────────
function MarkdownRenderer({ text }) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // h1 #
    if (line.startsWith('# ') && !line.startsWith('## ')) {
      elements.push(<h2 key={i} style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 800, color: 'var(--ink)', marginTop: 24, marginBottom: 12, paddingBottom: 8, borderBottom: '2px solid var(--line-card)' }}>{line.slice(2)}</h2>);
    }
    // h2 ##
    else if (line.startsWith('## ') && !line.startsWith('### ')) {
      elements.push(<h3 key={i} style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginTop: 20, marginBottom: 8, paddingLeft: 12, borderLeft: '3px solid var(--ink)' }}>{line.slice(3)}</h3>);
    }
    // h3 ###
    else if (line.startsWith('### ')) {
      elements.push(<h4 key={i} style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-2)', marginTop: 14, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: 'var(--font-mono)' }}>{line.slice(4)}</h4>);
    }
    // Horizontal rule ---
    else if (line.trim() === '---') {
      elements.push(<hr key={i} style={{ border: 'none', borderTop: '1px solid var(--line-card)', margin: '16px 0' }} />);
    }
    // Checkbox list item - [ ] **text**
    else if (line.match(/^\s*- \[ \]/)) {
      const text = line.replace(/^\s*- \[ \]\s*/, '');
      elements.push(
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '5px 0 5px 8px', borderBottom: '1px solid var(--line-card)' }}>
          <div style={{ width: 16, height: 16, border: '1.5px solid var(--muted-2)', borderRadius: 0, flexShrink: 0, marginTop: 2 }} />
          <span style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55 }}><InlineMD text={text} /></span>
        </div>
      );
    }
    // Sub-bullet (indented - )
    else if (line.match(/^\s{2,}- /)) {
      const text = line.replace(/^\s+- /, '');
      elements.push(
        <div key={i} style={{ display: 'flex', gap: 8, paddingLeft: 32, paddingTop: 3, paddingBottom: 3 }}>
          <span style={{ color: 'var(--muted-2)', flexShrink: 0 }}>·</span>
          <span style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.55 }}><InlineMD text={text} /></span>
        </div>
      );
    }
    // Regular bullet - text
    else if (line.startsWith('- ') || line.startsWith('* ')) {
      const text = line.slice(2);
      elements.push(
        <div key={i} style={{ display: 'flex', gap: 8, padding: '4px 0', paddingLeft: 4 }}>
          <span style={{ color: 'var(--ink)', fontWeight: 700, flexShrink: 0 }}>·</span>
          <span style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.6 }}><InlineMD text={text} /></span>
        </div>
      );
    }
    // Numbered list
    else if (line.match(/^\d+\./)) {
      const num = line.match(/^(\d+)\./)[1];
      const text = line.replace(/^\d+\.\s*/, '');
      elements.push(
        <div key={i} style={{ display: 'flex', gap: 10, padding: '4px 0', paddingLeft: 4 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)', minWidth: 20, paddingTop: 2 }}>{num}.</span>
          <span style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.6 }}><InlineMD text={text} /></span>
        </div>
      );
    }
    // Bold standalone line **text**
    else if (line.startsWith('**') && line.endsWith('**') && line.length > 4) {
      elements.push(<p key={i} style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', margin: '8px 0 4px' }}>{line.slice(2, -2)}</p>);
    }
    // Empty line
    else if (line.trim() === '') {
      elements.push(<div key={i} style={{ height: 6 }} />);
    }
    // Regular paragraph
    else {
      elements.push(<p key={i} style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.7, margin: '3px 0' }}><InlineMD text={line} /></p>);
    }
    i++;
  }
  return <div>{elements}</div>;
}

function InlineMD({ text }) {
  // Handle **bold** inline
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**')
          ? <strong key={i} style={{ fontWeight: 700, color: 'var(--ink)' }}>{part.slice(2, -2)}</strong>
          : <span key={i}>{part}</span>
      )}
    </>
  );
}

// ── SAMPLE SIZE CALCULATOR (FREE) ─────────────────────────────────
function SampleSizeCalculator() {
  const [type, setType] = useState('continuous');
  const [alpha, setAlpha] = useState(0.05);
  const [power, setPower] = useState(0.80);
  const [sd, setSd] = useState('');
  const [delta, setDelta] = useState('');
  const [p1, setP1] = useState('');
  const [p2, setP2] = useState('');
  const [dropout, setDropout] = useState(10);
  const [result, setResult] = useState(null);

  const zAlpha = { 0.05: 1.96, 0.01: 2.576, 0.10: 1.645 };
  const zBeta = { 0.80: 0.842, 0.90: 1.282, 0.95: 1.645 };

  const calculate = () => {
    const za = zAlpha[alpha] || 1.96;
    const zb = zBeta[power] || 0.842;
    let n;
    if (type === 'continuous') {
      if (!sd || !delta) return;
      n = (2 * Math.pow(parseFloat(sd), 2) * Math.pow(za + zb, 2)) / Math.pow(parseFloat(delta), 2);
    } else {
      if (!p1 || !p2) return;
      const pp1 = parseFloat(p1) / 100, pp2 = parseFloat(p2) / 100;
      const pbar = (pp1 + pp2) / 2;
      n = Math.pow(za + zb, 2) * (pp1 * (1 - pp1) + pp2 * (1 - pp2)) / Math.pow(pp1 - pp2, 2);
    }
    const nPerGroup = Math.ceil(n);
    const nInflated = Math.ceil(nPerGroup / (1 - dropout / 100));
    setResult({ nPerGroup, nInflated, total: nPerGroup * 2, totalInflated: nInflated * 2 });
  };

  const inp = { width: '100%', padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 0, fontSize: 14, boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 5, fontFamily: 'var(--font-mono)', letterSpacing: '1px', textTransform: 'uppercase' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {[['continuous', 'Continuous Endpoint'], ['binary', 'Binary (Proportion)']].map(([v, l]) => (
          <button key={v} onClick={() => { setType(v); setResult(null); }}
            style={{ padding: '7px 16px', border: `1px solid ${type === v ? 'var(--ink)' : 'var(--line)'}`, background: type === v ? 'var(--ink)' : 'var(--card)', color: type === v ? 'var(--paper)' : 'var(--muted)', borderRadius: 0, cursor: 'pointer', fontSize: 13, fontFamily: 'var(--font-mono)' }}>{l}</button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={lbl}>Significance Level (α)</label>
          <select style={inp} value={alpha} onChange={e => setAlpha(parseFloat(e.target.value))}>
            <option value={0.05}>0.05 (standard)</option>
            <option value={0.01}>0.01 (stringent)</option>
            <option value={0.10}>0.10 (exploratory)</option>
          </select>
        </div>
        <div>
          <label style={lbl}>Statistical Power (1-β)</label>
          <select style={inp} value={power} onChange={e => setPower(parseFloat(e.target.value))}>
            <option value={0.80}>80% (standard)</option>
            <option value={0.90}>90% (regulatory preference)</option>
            <option value={0.95}>95% (high confidence)</option>
          </select>
        </div>

        {type === 'continuous' ? (
          <>
            <div>
              <label style={lbl}>Standard Deviation (σ)</label>
              <input style={inp} type="number" value={sd} onChange={e => setSd(e.target.value)} placeholder="e.g. 15" />
            </div>
            <div>
              <label style={lbl}>Minimum Effect (δ)</label>
              <input style={inp} type="number" value={delta} onChange={e => setDelta(e.target.value)} placeholder="e.g. 5" />
            </div>
          </>
        ) : (
          <>
            <div>
              <label style={lbl}>Control Proportion (%)</label>
              <input style={inp} type="number" value={p1} onChange={e => setP1(e.target.value)} placeholder="e.g. 30" />
            </div>
            <div>
              <label style={lbl}>Treatment Proportion (%)</label>
              <input style={inp} type="number" value={p2} onChange={e => setP2(e.target.value)} placeholder="e.g. 50" />
            </div>
          </>
        )}
        <div>
          <label style={lbl}>Expected Dropout (%)</label>
          <input style={inp} type="number" value={dropout} onChange={e => setDropout(parseFloat(e.target.value))} placeholder="e.g. 10" />
        </div>
      </div>

      <button onClick={calculate} style={{ background: 'var(--ink)', color: 'var(--paper)', border: 'none', padding: '11px 28px', borderRadius: 0, cursor: 'pointer', fontSize: 14, fontWeight: 700, marginBottom: 20 }}>
        Calculate Sample Size
      </button>

      {result && (
        <div style={{ background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 0, padding: '20px 24px' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 16 }}>Results</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
            {[
              ['Per Group (pre-dropout)', result.nPerGroup],
              ['Per Group (inflated)', result.nInflated],
              ['Total (pre-dropout)', result.total],
              ['Total (inflated)', result.totalInflated],
            ].map(([label, val]) => (
              <div key={label} style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '12px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>{val}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 16, marginBottom: 0 }}>
            ⚠️ This calculator is for educational purposes. Always have a statistician review your sample size calculations before finalizing a protocol.
          </p>
        </div>
      )}
    </div>
  );
}

// ── AE GRADING TOOL (FREE) ────────────────────────────────────────
function AEGradingTool() {
  const CTCAE = {
    'Hematologic': [
      { name: 'Anemia', grades: ['Hgb <LLN–10 g/dL', 'Hgb 8–<10 g/dL', 'Hgb <8 g/dL; transfusion indicated', 'Life-threatening; urgent intervention', 'Death'] },
      { name: 'Neutropenia (ANC)', grades: ['<LLN–1500/mm³', '1000–<1500/mm³', '500–<1000/mm³', '<500/mm³', 'Death'] },
      { name: 'Thrombocytopenia', grades: ['<LLN–75,000/mm³', '50,000–<75,000/mm³', '25,000–<50,000/mm³', '<25,000/mm³', 'Death'] },
    ],
    'Hepatic': [
      { name: 'ALT Elevation', grades: ['>ULN–3×ULN', '>3–5×ULN', '>5–20×ULN', '>20×ULN', 'Death'] },
      { name: 'AST Elevation', grades: ['>ULN–3×ULN', '>3–5×ULN', '>5–20×ULN', '>20×ULN', 'Death'] },
      { name: 'Bilirubin Elevation', grades: ['>ULN–1.5×ULN', '>1.5–3×ULN', '>3–10×ULN', '>10×ULN', 'Death'] },
    ],
    'Gastrointestinal': [
      { name: 'Nausea', grades: ['Loss of appetite, no intake change', 'Decreased oral intake; IV fluids <24h', 'Inadequate caloric/fluid intake; IV fluids ≥24h', 'Life-threatening', 'Death'] },
      { name: 'Diarrhea', grades: ['<4 stools/day over baseline', '4–6 stools/day; IV fluids <24h', '≥7 stools/day; incontinence; IV ≥24h', 'Life-threatening', 'Death'] },
    ],
    'Cardiac': [
      { name: 'QTc Prolongation', grades: ['450–480 ms', '481–500 ms', '>500 ms; ≥60ms from baseline', 'Torsades/ventricular arrhythmia', 'Death'] },
      { name: 'Decreased LVEF', grades: ['10–20% drop; ≥LLN', '>20% drop; <LLN; sx', 'Symptomatic CHF; intervention', 'Refractory CHF; ≥Grade 3', 'Death'] },
    ],
  };

  const [selected, setSelected] = useState(null);
  const GRADE_COLORS = ['#16a34a', '#d97706', '#ea580c', '#dc2626', '#7f1d1d'];
  const GRADE_BG = ['#f0fdf4', '#fffbeb', '#fff7ed', '#fef2f2', '#fff1f2'];

  return (
    <div>
      <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>Select any adverse event to view CTCAE v5.0 grading criteria. Grade 1=Mild, Grade 2=Moderate, Grade 3=Severe, Grade 4=Life-threatening, Grade 5=Death.</p>
      {Object.entries(CTCAE).map(([system, events]) => (
        <div key={system} style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '2px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 8 }}>{system}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {events.map(ev => (
              <div key={ev.name}>
                <div onClick={() => setSelected(selected === ev.name ? null : ev.name)}
                  style={{ background: 'var(--card)', border: `1px solid ${selected === ev.name ? 'var(--ink)' : 'var(--line-card)'}`, borderRadius: 0, padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{ev.name}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>{selected === ev.name ? '▲' : '▼'} view grades</span>
                </div>
                {selected === ev.name && (
                  <div style={{ border: '1px solid var(--line-card)', borderTop: 'none', borderRadius: 0, padding: 0, overflow: 'hidden' }}>
                    {ev.grades.map((def, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px', background: GRADE_BG[i], borderBottom: i < 4 ? '1px solid var(--line-card)' : 'none' }}>
                        <span style={{ background: GRADE_COLORS[i], color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 0, flexShrink: 0, fontFamily: 'var(--font-mono)' }}>G{i + 1}</span>
                        <span style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>{def}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── PHASE GUIDE (FREE) ────────────────────────────────────────────
function PhaseGuide() {
  const [activePhase, setActivePhase] = useState('1');
  const phases = {
    '0': { label: 'Phase 0', color: '#6b7280', subjects: '10–15', duration: '< 1 year', objective: 'Microdose PK/PD only. No therapeutic intent. Subtherapeutic doses.', endpoints: 'Drug exposure (Cmax, AUC), target binding', population: 'Healthy volunteers or cancer patients' },
    '1': { label: 'Phase I', color: '#7c3aed', subjects: '20–80', duration: '1–2 years', objective: 'First-in-human safety and tolerability. PK/PD characterization. MTD/RP2D determination.', endpoints: 'DLTs, MTD/RP2D, PK parameters, PD biomarkers', population: 'Healthy volunteers (small molecules) or patients (oncology/toxic drugs)' },
    '2': { label: 'Phase II', color: '#1d4ed8', subjects: '100–500', duration: '2–3 years', objective: 'Preliminary efficacy evidence (proof of concept). Dose selection for Phase III.', endpoints: 'Surrogate/biomarker endpoints (ORR, PFS), dose-response', population: 'Target patient population — narrowly defined' },
    '3': { label: 'Phase III', color: '#065f46', subjects: '300–3000+', duration: '3–5 years', objective: 'Confirmatory efficacy and safety. Regulatory approval basis. Two adequate and well-controlled studies typically required.', endpoints: 'Clinical endpoints (OS, DFS, symptom improvement), QoL, safety', population: 'Broad target population — multicenter, multinational' },
    '4': { label: 'Phase IV', color: '#92400e', subjects: '1000+', duration: 'Ongoing', objective: 'Post-marketing safety surveillance and effectiveness. PMR/PMC obligations. Long-term follow-up.', endpoints: 'Real-world safety, effectiveness, HEOR, additional indications', population: 'General clinical practice population' },
  };
  const ph = phases[activePhase];
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 24, flexWrap: 'wrap' }}>
        {Object.entries(phases).map(([key, p]) => (
          <button key={key} onClick={() => setActivePhase(key)}
            style={{ padding: '8px 18px', border: `2px solid ${activePhase === key ? 'var(--ink)' : 'var(--line)'}`, background: activePhase === key ? 'var(--ink)' : 'var(--card)', color: activePhase === key ? 'var(--paper)' : 'var(--muted)', borderRadius: 0, cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.12s' }}>
            {p.label}
          </button>
        ))}
      </div>
      <div style={{ background: 'var(--card)', border: `2px solid var(--ink)`, borderRadius: 0, padding: '24px' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, color: 'var(--ink)', marginBottom: 16 }}>{ph.label}</div>
        {[['🎯 Objective', ph.objective], ['📊 Key Endpoints', ph.endpoints], ['👥 Population', ph.population], ['📏 Sample Size', ph.subjects], ['⏱️ Typical Duration', ph.duration]].map(([label, val]) => (
          <div key={label} style={{ display: 'flex', gap: 12, marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid var(--line-card)' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)', minWidth: 150, paddingTop: 2 }}>{label}</div>
            <div style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6 }}>{val}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── ADVANCED POWER CALCULATOR (PRO) ───────────────────────────────
function AdvancedPowerCalculator() {
  const [design, setDesign] = useState('survival');
  const [hr, setHr] = useState('');
  const [alpha, setAlpha] = useState(0.05);
  const [power, setPower] = useState(0.80);
  const [eventRate, setEventRate] = useState('');
  const [result, setResult] = useState(null);
  const zAlpha = { 0.05: 1.96, 0.025: 2.24, 0.01: 2.576 };
  const zBeta = { 0.80: 0.842, 0.90: 1.282, 0.95: 1.645 };

  const calculate = () => {
    const za = zAlpha[alpha] || 1.96;
    const zb = zBeta[power] || 0.842;
    if (design === 'survival' && hr && eventRate) {
      const lnHR = Math.log(parseFloat(hr));
      const events = Math.ceil(4 * Math.pow(za + zb, 2) / Math.pow(lnHR, 2));
      const n = Math.ceil(events / (parseFloat(eventRate) / 100));
      setResult({ events, n, total: n * 2 });
    }
  };

  const inp = { width: '100%', padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 0, fontSize: 14, boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 5, fontFamily: 'var(--font-mono)', letterSpacing: '1px', textTransform: 'uppercase' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['survival', 'noninferiority', 'crossover'].map(d => (
          <button key={d} onClick={() => { setDesign(d); setResult(null); }}
            style={{ padding: '7px 14px', border: `1px solid ${design === d ? 'var(--ink)' : 'var(--line)'}`, background: design === d ? 'var(--ink)' : 'var(--card)', color: design === d ? 'var(--paper)' : 'var(--muted)', borderRadius: 0, cursor: 'pointer', fontSize: 12, fontFamily: 'var(--font-mono)', textTransform: 'capitalize' }}>{d}</button>
        ))}
      </div>
      {design === 'survival' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
          <div><label style={lbl}>Hazard Ratio (treatment/control)</label><input style={inp} type="number" value={hr} onChange={e => setHr(e.target.value)} placeholder="e.g. 0.70" step="0.01" /></div>
          <div><label style={lbl}>Expected Event Rate (%)</label><input style={inp} type="number" value={eventRate} onChange={e => setEventRate(e.target.value)} placeholder="e.g. 60" /></div>
          <div><label style={lbl}>Significance Level (α)</label>
            <select style={inp} value={alpha} onChange={e => setAlpha(parseFloat(e.target.value))}>
              <option value={0.05}>0.05 two-sided</option><option value={0.025}>0.025 one-sided</option><option value={0.01}>0.01</option>
            </select>
          </div>
          <div><label style={lbl}>Power</label>
            <select style={inp} value={power} onChange={e => setPower(parseFloat(e.target.value))}>
              <option value={0.80}>80%</option><option value={0.90}>90%</option><option value={0.95}>95%</option>
            </select>
          </div>
        </div>
      )}
      {design === 'noninferiority' && <div style={{ padding: '20px', background: 'var(--paper-2)', borderRadius: 0, color: 'var(--muted)', fontSize: 13 }}>Non-inferiority calculator: Enter NI margin (Δ), expected difference, and variance. Uses standard NI power formula: N = (z_α + z_β)² × 2σ² / (δ - Δ)²</div>}
      {design === 'crossover' && <div style={{ padding: '20px', background: 'var(--paper-2)', borderRadius: 0, color: 'var(--muted)', fontSize: 13 }}>Crossover design calculator: N = (z_α/2 + z_β)² × 2σ_d² / δ² where σ_d is the within-subject SD of differences.</div>}
      {design === 'survival' && <button onClick={calculate} style={{ background: 'var(--ink)', color: 'var(--paper)', border: 'none', padding: '10px 24px', borderRadius: 0, cursor: 'pointer', fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Calculate</button>}
      {result && (
        <div style={{ background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 0, padding: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {[['Events Required', result.events], ['Per Arm', result.n], ['Total N', result.total]].map(([l, v]) => (
              <div key={l} style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: 26, fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--ink)' }}>{v}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--font-mono)' }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── AI PROTOCOL BUILDER (PRO) ─────────────────────────────────────
function AIProtocolBuilder() {
  const [form, setForm] = useState({ indication: '', phase: 'Phase II', design: 'RCT', primary: '', comparator: 'Placebo' });
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!form.indication || !form.primary) return;
    setLoading(true);
    setResult('');
    try {
      const resp = await fetch('/api/knowledge/protocol-builder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(form),
      });
      const data = await resp.json();
      setResult(data.outline || 'Failed to generate. Try again.');
    } catch { setResult('Error generating protocol outline.'); }
    setLoading(false);
  };

  const inp = { width: '100%', padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 0, fontSize: 13, boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 5, fontFamily: 'var(--font-mono)', letterSpacing: '1px', textTransform: 'uppercase' };

  return (
    <div>
      <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>Generate an ICH E6(R3)-compliant protocol framework. Review and customize for your specific study.</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div><label style={lbl}>Disease/Indication</label><input style={inp} value={form.indication} onChange={e => setForm({ ...form, indication: e.target.value })} placeholder="e.g. Type 2 Diabetes" /></div>
        <div><label style={lbl}>Phase</label>
          <select style={inp} value={form.phase} onChange={e => setForm({ ...form, phase: e.target.value })}>
            {['Phase I', 'Phase II', 'Phase IIb/III', 'Phase III', 'Phase IV'].map(p => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div><label style={lbl}>Primary Endpoint</label><input style={inp} value={form.primary} onChange={e => setForm({ ...form, primary: e.target.value })} placeholder="e.g. HbA1c reduction at 24 weeks" /></div>
        <div><label style={lbl}>Comparator</label>
          <select style={inp} value={form.comparator} onChange={e => setForm({ ...form, comparator: e.target.value })}>
            {['Placebo', 'Active Control', 'Standard of Care', 'Open-Label'].map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <button onClick={generate} disabled={loading} style={{ background: loading ? 'var(--muted)' : 'var(--ink)', color: 'var(--paper)', border: 'none', padding: '11px 24px', borderRadius: 0, cursor: loading ? 'default' : 'pointer', fontSize: 14, fontWeight: 700, marginBottom: 20 }}>
        {loading ? '🤖 Generating protocol...' : '🤖 Generate Protocol Outline'}
      </button>
      {result && (
        <div style={{ background: 'var(--paper)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '20px 24px', maxHeight: 600, overflowY: 'auto' }}>
          <MarkdownRenderer text={result} />
        </div>
      )}
    </div>
  );
}

// ── AI IRB CHECKLIST (PRO) ────────────────────────────────────────
function AIIRBChecklist() {
  const [form, setForm] = useState({ trialType: 'Drug', phase: 'Phase II', population: 'Adults', risk: 'Greater than minimal' });
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    setResult('');
    try {
      const resp = await fetch('/api/knowledge/irb-checklist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(form),
      });
      const data = await resp.json();
      setResult(data.checklist || 'Failed to generate.');
    } catch { setResult('Error generating checklist.'); }
    setLoading(false);
  };

  const inp = { width: '100%', padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 0, fontSize: 13, boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 5, fontFamily: 'var(--font-mono)', letterSpacing: '1px', textTransform: 'uppercase' };

  return (
    <div>
      <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>Generate a customized IRB submission checklist based on your trial parameters.</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div><label style={lbl}>Trial Type</label>
          <select style={inp} value={form.trialType} onChange={e => setForm({ ...form, trialType: e.target.value })}>
            {['Drug', 'Device', 'Biologic', 'Behavioral', 'Survey/Observational'].map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div><label style={lbl}>Phase</label>
          <select style={inp} value={form.phase} onChange={e => setForm({ ...form, phase: e.target.value })}>
            {['Phase I', 'Phase II', 'Phase III', 'Phase IV', 'Observational'].map(p => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div><label style={lbl}>Population</label>
          <select style={inp} value={form.population} onChange={e => setForm({ ...form, population: e.target.value })}>
            {['Adults', 'Pediatrics', 'Pregnant Women', 'Prisoners', 'Cognitively Impaired'].map(p => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div><label style={lbl}>Risk Level</label>
          <select style={inp} value={form.risk} onChange={e => setForm({ ...form, risk: e.target.value })}>
            {['Minimal risk', 'Greater than minimal', 'Significant risk device'].map(r => <option key={r}>{r}</option>)}
          </select>
        </div>
      </div>
      <button onClick={generate} disabled={loading} style={{ background: loading ? 'var(--muted)' : 'var(--ink)', color: 'var(--paper)', border: 'none', padding: '11px 24px', borderRadius: 0, cursor: loading ? 'default' : 'pointer', fontSize: 14, fontWeight: 700, marginBottom: 20 }}>
        {loading ? '🤖 Generating checklist...' : '🤖 Generate IRB Checklist'}
      </button>
      {result && (
        <div style={{ background: 'var(--paper)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '20px 24px', maxHeight: 600, overflowY: 'auto' }}>
          <MarkdownRenderer text={result} />
        </div>
      )}
    </div>
  );
}

// ── CONSORT CHECKLIST (PRO) ───────────────────────────────────────
function CONSORTChecklist() {
  const items = [
    { section: 'Title & Abstract', items: ['Identification as RCT in title', 'Structured abstract: trial design, methods, results, conclusions'] },
    { section: 'Background', items: ['Scientific background and explanation of rationale', 'Specific objectives or hypotheses'] },
    { section: 'Methods — Participants', items: ['Eligibility criteria', 'Settings and locations of data collection'] },
    { section: 'Methods — Interventions', items: ['Interventions for each group with sufficient detail for replication', 'How and when they were administered'] },
    { section: 'Methods — Outcomes', items: ['Pre-specified primary and secondary outcomes', 'How and when outcomes were assessed', 'Changes to outcomes after trial commencement'] },
    { section: 'Methods — Sample Size', items: ['How sample size was determined', 'Explanation of interim analyses and stopping guidelines'] },
    { section: 'Methods — Randomization', items: ['Method of generating allocation sequence', 'Type of randomisation (block, stratification, etc.)', 'Mechanism of allocation concealment', 'Who generated sequence, enrolled, assigned'] },
    { section: 'Methods — Blinding', items: ['Who was blinded after assignment', 'Similarity of interventions', 'Mechanism of unblinding if relevant'] },
    { section: 'Methods — Statistical', items: ['Statistical methods for primary and secondary outcomes', 'Methods for additional analyses (subgroups, adjusted)', 'Missing data handling'] },
    { section: 'Results — Participant Flow', items: ['Numbers in each stage (CONSORT diagram)', 'Losses and exclusions after randomisation'] },
    { section: 'Results — Baseline', items: ['Baseline demographics and clinical characteristics'] },
    { section: 'Results — Numbers Analyzed', items: ['Number analyzed in each group', 'Deviations from protocol'] },
    { section: 'Results — Outcomes', items: ['Results for each primary and secondary outcome', 'Effect size with confidence intervals for primary outcome'] },
    { section: 'Results — Harms', items: ['All important harms or unintended effects in each group'] },
    { section: 'Discussion', items: ['Trial limitations', 'Generalizability (external validity)', 'Interpretation consistent with results'] },
    { section: 'Other', items: ['Registration number and registry name', 'Where full protocol can be accessed', 'Funding and other support'] },
  ];

  const [checked, setChecked] = useState({});
  const total = items.reduce((s, s2) => s + s2.items.length, 0);
  const done = Object.values(checked).filter(Boolean).length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, padding: '12px 16px', background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 0 }}>
        <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--ink)' }}>{done}/{total}</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>CONSORT 2010 Checklist</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--font-mono)' }}>{Math.round(done / total * 100)}% complete</div>
        </div>
        <div style={{ flex: 1, height: 6, background: 'var(--line)', borderRadius: 0, overflow: 'hidden', marginLeft: 8 }}>
          <div style={{ height: '100%', background: 'var(--ink)', width: `${done / total * 100}%`, transition: 'width 0.3s' }} />
        </div>
      </div>
      {items.map(section => (
        <div key={section.section} style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted-2)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 6 }}>{section.section}</div>
          {section.items.map((item, i) => {
            const key = `${section.section}:${i}`;
            return (
              <div key={key} onClick={() => setChecked(prev => ({ ...prev, [key]: !prev[key] }))}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px', background: checked[key] ? 'var(--paper-2)' : 'var(--card)', border: `1px solid ${checked[key] ? 'var(--ink)' : 'var(--line-card)'}`, borderRadius: 0, marginBottom: 2, cursor: 'pointer' }}>
                <div style={{ width: 18, height: 18, border: `2px solid ${checked[key] ? 'var(--ink)' : 'var(--muted-2)'}`, borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: checked[key] ? 'var(--ink)' : 'var(--card)', marginTop: 1 }}>
                  {checked[key] && <span style={{ color: 'var(--paper)', fontSize: 11, fontWeight: 700 }}>✓</span>}
                </div>
                <span style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>{item}</span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ── RANDOMIZATION GENERATOR (PRO) ────────────────────────────────
function RandomizationGenerator() {
  const [arms, setArms] = useState(2);
  const [blockSize, setBlockSize] = useState(4);
  const [n, setN] = useState(20);
  const [list, setList] = useState([]);

  const generate = () => {
    const armLabels = ['A', 'B', 'C', 'D'].slice(0, arms);
    const blocks = [];
    let total = 0;
    while (total < n) {
      const block = [];
      const perArm = blockSize / arms;
      armLabels.forEach(a => { for (let i = 0; i < perArm; i++) block.push(a); });
      // Fisher-Yates shuffle
      for (let i = block.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [block[i], block[j]] = [block[j], block[i]];
      }
      blocks.push(...block);
      total += blockSize;
    }
    setList(blocks.slice(0, n).map((arm, i) => ({ id: i + 1, arm, block: Math.floor(i / blockSize) + 1 })));
  };

  const ARM_COLORS = { A: '#1d4ed8', B: '#15803d', C: '#b91c1c', D: '#7c3aed' };

  return (
    <div>
      <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>Generate a block randomization list. In real trials, use validated software (SAS, R, PROC PLAN) and maintain confidential seed numbers.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        <div><label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 5, fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>Number of Arms</label>
          <select style={{ width: '100%', padding: '8px', border: '1px solid var(--line)', borderRadius: 0, fontSize: 14 }} value={arms} onChange={e => setArms(parseInt(e.target.value))}>
            {[2, 3, 4].map(a => <option key={a} value={a}>{a} arms</option>)}
          </select>
        </div>
        <div><label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 5, fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>Block Size</label>
          <select style={{ width: '100%', padding: '8px', border: '1px solid var(--line)', borderRadius: 0, fontSize: 14 }} value={blockSize} onChange={e => setBlockSize(parseInt(e.target.value))}>
            {[2, 4, 6, 8].map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div><label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 5, fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>Total Subjects</label>
          <input type="number" style={{ width: '100%', padding: '8px', border: '1px solid var(--line)', borderRadius: 0, fontSize: 14, boxSizing: 'border-box' }} value={n} onChange={e => setN(parseInt(e.target.value))} min={4} max={200} />
        </div>
      </div>
      <button onClick={generate} style={{ background: 'var(--ink)', color: 'var(--paper)', border: 'none', padding: '10px 24px', borderRadius: 0, cursor: 'pointer', fontSize: 14, fontWeight: 700, marginBottom: 20 }}>Generate List</button>
      {list.length > 0 && (
        <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid var(--line-card)', borderRadius: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: 'var(--paper-2)', position: 'sticky', top: 0 }}>
              <tr>{['Subject #', 'Randomization Assignment', 'Block'].map(h => <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--muted)', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', borderBottom: '1px solid var(--line-card)' }}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {list.map(row => (
                <tr key={row.id} style={{ borderBottom: '1px solid var(--line-card)' }}>
                  <td style={{ padding: '8px 12px', fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--ink)' }}>{String(row.id).padStart(3, '0')}</td>
                  <td style={{ padding: '8px 12px' }}><span style={{ background: ARM_COLORS[row.arm] + '22', color: ARM_COLORS[row.arm], fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700, padding: '3px 12px', borderRadius: 0 }}>Arm {row.arm}</span></td>
                  <td style={{ padding: '8px 12px', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--muted)' }}>Block {row.block}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── RISK MATRIX (PRO) ─────────────────────────────────────────────
function RiskMatrix() {
  const defaultRisks = [
    { id: 1, area: 'Protocol Compliance', risk: 'Investigator protocol deviations', likelihood: 3, impact: 4, mitigation: 'Enhanced site training, protocol deviation tracking dashboard' },
    { id: 2, area: 'Data Integrity', risk: 'Missing or incomplete CRF data', likelihood: 3, impact: 3, mitigation: 'Automated data checks, query management, CRA monitoring' },
    { id: 3, area: 'Subject Safety', risk: 'Delayed SAE reporting', likelihood: 2, impact: 5, mitigation: '24-hour SAE reporting SOP, investigator training, sponsor-side safety tracking' },
    { id: 4, area: 'Enrollment', risk: 'Slow site activation / low recruitment', likelihood: 4, impact: 4, mitigation: 'Site feasibility assessment, backup sites, patient advocacy partnerships' },
    { id: 5, area: 'Regulatory', risk: 'IRB amendment delays', likelihood: 2, impact: 3, mitigation: 'Protocol review for amendment triggers, proactive IRB communication' },
  ];
  const [risks, setRisks] = useState(defaultRisks);
  const score = (r) => r.likelihood * r.impact;
  const color = (s) => s >= 15 ? '#dc2626' : s >= 9 ? '#f59e0b' : '#16a34a';
  const label = (s) => s >= 15 ? 'HIGH' : s >= 9 ? 'MEDIUM' : 'LOW';

  return (
    <div>
      <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>ICH E6(R3) Risk-Based Quality Management (RBQM) risk register. Likelihood 1-5 × Impact 1-5 = Risk Score. Red ≥15 (High), Amber 9–14 (Medium), Green &lt;9 (Low).</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {risks.map(risk => (
          <div key={risk.id} style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '14px 16px' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 11, color: 'var(--muted-2)', fontFamily: 'var(--font-mono)', marginBottom: 3, letterSpacing: '1.5px', textTransform: 'uppercase' }}>{risk.area}</div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: 'var(--ink)' }}>{risk.risk}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>↳ {risk.mitigation}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {[['L', 'likelihood'], ['I', 'impact']].map(([abbr, key]) => (
                  <div key={key} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--font-mono)', marginBottom: 3 }}>{abbr}</div>
                    <select value={risk[key]} onChange={e => setRisks(prev => prev.map(r => r.id === risk.id ? { ...r, [key]: parseInt(e.target.value) } : r))}
                      style={{ width: 48, padding: '4px', border: '1px solid var(--line)', borderRadius: 0, fontSize: 14, textAlign: 'center' }}>
                      {[1, 2, 3, 4, 5].map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                ))}
                <div style={{ textAlign: 'center', minWidth: 60 }}>
                  <div style={{ fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--font-mono)', marginBottom: 3 }}>SCORE</div>
                  <div style={{ background: color(score(risk)), color: '#fff', fontWeight: 700, fontSize: 13, padding: '4px 10px', borderRadius: 0, fontFamily: 'var(--font-mono)' }}>
                    {score(risk)} {label(score(risk))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}